import { useEffect, useState, cloneElement } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col, Label, Form } from "reactstrap";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import moment from "moment";
import { process } from "@progress/kendo-data-query";
import { ColumnMenu } from "../components/kendoComponents/columnMenu/ColumnMenu";
import { GridPager } from "../components/kendoComponents/gridPager/GridPager";
import { ColumnProps } from "../components/kendoComponents/columnProps/ColumnProps";
import { useNavigate, useParams } from "react-router-dom";

import { useEffectOnChange } from "../components/hooks/CustomHooks";

import LoaderSpin from "../components/loaderSpin/LoaderSpin";

import FilterOverlay from "../components/filterOverlay/FilterOverlay";

import "../assets/scss/kendo-theme-custom.scss";
import {
  fetchLastProcessRuns,
  fetchProcessVersionRunInfo,
} from "../store/app/ProcessRuns/ProcessRunsSlice";
import DateCell from "../components/kendoComponents/dateCell/DateCell";

const LatestRuns = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { processId } = useParams();

  const refreshCount = useSelector(
    (state) => state.dataQualityReducer.refreshCount
  );
  const searchParams = new URLSearchParams(location.search);
  const lastProcessRunsList = useSelector(
    (state) => state.processRunsReducer.lastProcessRunsList
  );
  const processVersionRunInfo = useSelector(
    (state) => state.processRunsReducer.processVersionRunInfo || {}
  );

  const filterIntervallo = useSelector(
    (state) => state.dataQualityReducer.filterIntervallo
  );
  const dataCheck = useSelector((state) => state.dataQualityReducer.dataCheck);

  const isSpinnerVisible = useSelector(
    (state) => state.dataQualityReducer.loading
  );

  const [selectedVersionId, setSelectedVersionId] = useState(null);

  const [dataState, setDataState] = useState({
    sort: [
      {
        field: "code",
        dir: "asc",
      },
    ],
    skip: 0,
    take: 20,
  });

  const [dataResult, setDataResult] = useState(
    process(lastProcessRunsList, dataState)
  );

  useEffect(() => {
    const processParam = searchParams.get("process");
    const processValue = processParam ? JSON.parse(processParam)[0] : "";
    dispatch(fetchLastProcessRuns(processId, processValue, filterIntervallo));
  }, [refreshCount, navigate]);

  useEffect(() => {
    setDataState((prev) => ({
      ...{
        sort: [
          {
            field: "code",
            dir: "asc",
          },
        ],
      },
      skip: 0,
      take: 20,
    }));
    setDataResult(process(lastProcessRunsList, dataState));
  }, [lastProcessRunsList]);

  useEffectOnChange(() => {
    if (lastProcessRunsList.length === 0) return;

    dispatch(fetchProcessVersionRunInfo(lastProcessRunsList[0]?.version_id));
    setSelectedVersionId(lastProcessRunsList[0]?.version_id);
  }, [lastProcessRunsList]);

  const handleRowClick = (version_id) => {
    if (lastProcessRunsList.length === 0) return;

    setSelectedVersionId(version_id);

    dispatch(fetchProcessVersionRunInfo(version_id));
  };

  return (
    <>
      <Form>
        <Row className="bg-light-primary px-4 py-2">
          <Col>
            <Row className="align-items-center">
              <Col md="3" lg="2" className="d-flex">
                <h5 className="form-row-title">Process Info</h5>
              </Col>

              <Col md="auto">
                <Label for="outputVersionID">Version</Label>
                <button
                  id="outputVersionID"
                  className="pt-0 form-control-plaintext hv-75"
                  onClick={() => {
                    navigate(
                      "/versioni/detail/" +
                      processVersionRunInfo.version_id +
                      "?" +
                      searchParams
                    );
                  }}
                >
                  <span className="px-2 bg-medium-primary border border-primary rounded">
                    {processVersionRunInfo.version_id}
                  </span>
                </button>
              </Col>

              <Col md="auto">
                <Label for="processName">Process</Label>
                <p id="processName" className="pt-0 form-control-plaintext">
                  {processVersionRunInfo.process_name}
                </p>
              </Col>
              <Col md="auto">
                <Label for="processType">Process Type</Label>
                <p id="processType" className="pt-0 form-control-plaintext">
                  {processVersionRunInfo.process_type}
                </p>
              </Col>
              <Col md="auto">
                <Label for="revisionNumber">Revision number</Label>
                <p id="revisionNumber" className="pt-0 form-control-plaintext">
                  {processVersionRunInfo.revision_num}
                </p>
              </Col>
              <Col md="auto">
                <Label for="releaseData">Revision Date</Label>
                <p id="releaseData" className="pt-0 form-control-plaintext">
                  {moment(processVersionRunInfo.relase_date).format(
                    "DD/MM/YYYY"
                  )}
                </p>
              </Col>
              <Col md="auto">
                <Label for="revisionReleaseNotes">Revision notes</Label>
                <p
                  id="revisionReleaseNotes"
                  className="pt-0 form-control-plaintext"
                >
                  {processVersionRunInfo.release_notes || "N/A"}
                </p>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>

      <Row className="px-4 my-3"></Row>

      <Container fluid className="py-2 px-4  position-relative">
        <Row>
          <Col>
            <div className="grid_task_container border">
              <Grid
                id="latestRunsGrid"
                style={{ height: "100%" }}
                pageable={true}
                sortable={true}
                total={dataResult?.length}
                data={dataResult}
                {...dataState}
                groupable={true}
                pager={(props) => GridPager(props, lastProcessRunsList)}
                onDataStateChange={(e) => {
                  setDataState(e.dataState);
                  setDataResult(process(lastProcessRunsList, e.dataState));
                }}
                rowRender={(row, props) => {
                  const isSelected =
                    props.dataItem.version_id === selectedVersionId;
                  return cloneElement(
                    row,
                    {
                      style: {
                        ...row.props.style,
                        background: isSelected
                          ? "#ffeeba"
                          : row.props.style?.background,
                      },
                      onClick: () => handleRowClick(props.dataItem.version_id),
                    },
                    row.props.children
                  );
                }}
              >
                <Column
                  {...ColumnProps("version_id", dataState)}
                  title="Version"
                  columnMenu={ColumnMenu}
                  width={120}
                  cell={(props) => (
                    <td>
                      <a
                        style={{ cursor: "pointer" }}
                        className="fw-bold text-decoration-underline"
                        onClick={(event) => {
                          navigate(
                            "/versioni/detail/" +
                            props.dataItem.version_id +
                            "?" +
                            searchParams
                          );
                        }}
                        onKeyDown={() => console.log("onKeyDown")}
                      >
                        {props.dataItem.version_id}
                      </a>
                    </td>
                  )}
                />
                <Column
                  {...ColumnProps("run_status", dataState)}
                  title="Status"
                  columnMenu={ColumnMenu}
                />
                <Column
                  {...ColumnProps("run_start_ts", dataState)}
                  title="Run Start"
                  columnMenu={ColumnMenu}
                  cell={(props) => DateCell(props, true)}
                />
                <Column
                  {...ColumnProps("run_end_ts", dataState)}
                  title="Run End"
                  columnMenu={ColumnMenu}
                  cell={(props) => DateCell(props, true)}
                />
                <Column
                  {...ColumnProps("release_notes", dataState)}
                  title="Release Notes"
                  columnMenu={ColumnMenu}
                />
                <Column
                  {...ColumnProps("duration", dataState)}
                  title="Duration"
                  columnMenu={ColumnMenu}
                />
              </Grid>
            </div>
          </Col>
        </Row>
        <LoaderSpin isVisible={isSpinnerVisible} />
        <FilterOverlay isVisible={dataCheck} />
      </Container>
    </>
  );
};


export default LatestRuns;
